// SAB-1046: a rival team's roster, opened from a standings row.
//
// SIZING follows SAB-1016 exactly rather than approximately: full-screen sheet on mobile,
// centered ~players-box modal from `sm`, ONE close button (SheetContent's built-in is
// suppressed with hideClose), safe-area inset on the header, Esc/backdrop via Radix.
// `side="bottom"` pins inset-x-0/bottom-0, so each has to be undone explicitly -- mx-auto
// alone will not centre it.
//
// ORDER comes from leagueSlotOrder(settings.roster_slots), the same source My Team and the
// SAB-1016 drop picker read. Not a local literal: a league with custom slots would render
// its roster in a different order here than on its own team page, and the fallback for a
// league with no roster_slots is load-bearing rather than cosmetic.
import { useEffect, useMemo, useState, type KeyboardEvent } from 'react';
import { ActiveNewsProvider } from '@/components/player/ActiveNewsContext';
import { NFLPlayerAvatar } from './NFLPlayerAvatar';
import { X } from 'lucide-react';
import { Sheet, SheetContent, SheetTitle, SheetDescription } from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { supabase } from '@/integrations/supabase/client';
import { cn } from '@/lib/utils';
import TeamLogo from './TeamLogo';
import { NFLPlayerMeta } from './NFLPlayerMeta';
import { leagueSlotOrder, slotLabel } from '@/lib/nflRosterSlots';

export interface OverlayTeam {
  id: string;
  name: string;
  owner_id: string;
  logo_url?: string | null;
  wins?: number | null;
  losses?: number | null;
  ties?: number | null;
  points_for?: number | string | null;
  points_against?: number | string | null;
}

interface Row {
  player_id: string;
  full_name: string;
  position: string | null;
  position_slot: string;
  nfl_team_abbr: string | null;
  injury_status: string | null;
  status: string | null;
  bye_week: number | null;
  headshot_url: string | null;
  weekly_proj: number | null;
}

interface Props {
  team: OverlayTeam | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  leagueId: string | null;
  rosterSlots: unknown;
  week?: number | null;
  /** True when this is the viewer's own team — the Trade button is withheld, not disabled. */
  isOwnTeam: boolean;
  /** Opens the existing trade flow pre-targeted at this team. */
  onTrade?: (teamId: string) => void;
  /**
   * SAB-1068. Opens the player-detail modal for a row. Optional, and a row is only
   * interactive when it is supplied AND the slot holds a player -- an empty slot must not
   * look tappable, and neither must a row with nowhere to go. Same rule as the Trade
   * button above it: render the affordance only when there is a handler behind it.
   */
  onPlayerClick?: (playerId: string) => void;
  /** SAB-1172: open this player's card on the News tab. Falls back to onPlayerClick. */
  onPlayerNews?: (playerId: string) => void;
}

const num = (v: unknown): number | null =>
  v === null || v === undefined || v === '' ? null : Number(v);

export function NFLTeamRosterOverlay({
  team, open, onOpenChange, leagueId, rosterSlots, week, isOwnTeam, onTrade, onPlayerClick, onPlayerNews,
}: Props) {
  const [rows, setRows] = useState<Row[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      if (!open || !team || !leagueId) return;
      setLoading(true);
      try {
        const { data } = await supabase
          .from('nfl_rosters' as never)
          .select('player_id, position_slot, nfl_players!inner(full_name, position, injury_status, status, nfl_franchise_id, headshot_url)')
          .eq('team_id', team.id)
          .eq('league_id', leagueId)
          .eq('is_active', true);
        const raw = (data ?? []) as unknown as {
          player_id: string; position_slot: string;
          nfl_players: { full_name: string; position: string | null; injury_status: string | null; status: string | null; nfl_franchise_id: string | null; headshot_url: string | null } | null;
        }[];

        // Franchise alias + bye in one read for the whole roster, not per row. The alias
        // and the bye both live here, and SAB-1003's meta line needs both.
        const fids = [...new Set(raw.map(r => r.nfl_players?.nfl_franchise_id).filter(Boolean))] as string[];
        const franchise = new Map<string, { alias: string | null; bye: number | null }>();
        if (fids.length) {
          const { data: f } = await supabase
            .from('nfl_franchises' as never)
            .select('id, alias, bye_week_2026')
            .in('id', fids);
          for (const row of ((f ?? []) as unknown as { id: string; alias: string | null; bye_week_2026: number | null }[])) {
            franchise.set(row.id, { alias: row.alias, bye: row.bye_week_2026 });
          }
        }

        // Weekly projection from the SAME view the players list sorts on, so the number
        // here and the number there cannot disagree.
        const proj = new Map<string, number | null>();
        if (week && raw.length) {
          const { data: p } = await supabase
            .from('v_nfl_weekly_projections' as never)
            .select('player_id, weekly_proj')
            .eq('week', week)
            .in('player_id', raw.map(r => r.player_id));
          for (const row of ((p ?? []) as unknown as { player_id: string; weekly_proj: number | null }[])) {
            proj.set(row.player_id, num(row.weekly_proj));
          }
        }

        if (cancelled) return;
        setRows(raw.map(r => {
          const fr = r.nfl_players?.nfl_franchise_id ? franchise.get(r.nfl_players.nfl_franchise_id) : undefined;
          return {
            player_id: r.player_id,
            full_name: r.nfl_players?.full_name ?? 'Unknown',
            position: r.nfl_players?.position ?? null,
            position_slot: r.position_slot,
            nfl_team_abbr: fr?.alias ?? null,
            injury_status: r.nfl_players?.injury_status ?? null,
            status: r.nfl_players?.status ?? null,
            bye_week: fr?.bye ?? null,
            headshot_url: r.nfl_players?.headshot_url ?? null,
            weekly_proj: proj.get(r.player_id) ?? null,
          };
        }));
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    void load();
    return () => { cancelled = true; };
  }, [open, team, leagueId, week]);

  /**
   * Slot order, then the players placed INTO it — not the roster rows sorted.
   *
   * An empty declared slot has to render as an empty row, or a team carrying 16 of 19
   * silently looks like a full one three rows shorter. Same reason the drop picker walks
   * the template rather than the roster.
   */
  const ordered = useMemo(() => {
    const template = leagueSlotOrder(rosterSlots);
    const byslot = new Map<string, Row>();
    const extras: Row[] = [];
    for (const r of rows) {
      if (!byslot.has(r.position_slot)) byslot.set(r.position_slot, r);
      // A slot the template does not declare still holds a real player. Appending is the
      // honest answer; dropping them would hide a roster the league can actually field.
      else extras.push(r);
    }
    const out = template.map(slot => ({ slot, player: byslot.get(slot) ?? null }));
    for (const r of rows) if (!template.includes(r.position_slot)) out.push({ slot: r.position_slot, player: r });
    for (const r of extras) out.push({ slot: r.position_slot, player: r });
    return out;
  }, [rows, rosterSlots]);

  if (!team) return null;

  const rec = `${team.wins ?? 0}-${team.losses ?? 0}${team.ties ? `-${team.ties}` : ''}`;
  const pf = num(team.points_for), pa = num(team.points_against);

  return (
    <ActiveNewsProvider sport="nfl" keys={rows.map(r => r.player_id)}>
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="bottom"
        data-testid="team-roster-overlay"
        hideClose
        className={cn(
          'p-0 flex flex-col gap-0 bg-background',
          'h-[100dvh]',
          'sm:inset-x-auto sm:bottom-auto sm:left-1/2 sm:top-1/2',
          'sm:-translate-x-1/2 sm:-translate-y-1/2',
          'sm:w-[calc(100vw-4rem)] sm:max-w-2xl sm:h-[85vh] sm:rounded-xl sm:border',
          'sm:data-[state=open]:slide-in-from-bottom-0 sm:data-[state=closed]:slide-out-to-bottom-0',
        )}
      >
        <SheetTitle className="sr-only">{team.name} roster</SheetTitle>
        <SheetDescription className="sr-only">
          {team.name}, {rec}. Full roster in slot order.
        </SheetDescription>

        {/* pt-safe-top: the sheet is full-height on a phone, so in the WRAPPED APP its top
            edge is the physical top of the screen. env() resolves to 0px in a browser, so
            the same markup is correct in both. */}
        <div className="pt-safe-top flex-none border-b border-border px-4 pb-3 pt-3 flex items-center gap-3">
          <TeamLogo logoUrl={team.logo_url} name={team.name} className="h-10 w-10 flex-none" textClassName="text-sm" />
          {/* min-w-0 is what lets the name truncate instead of shoving the buttons off the
              row -- SAB-1005. The stress case is "Winston, You Lose Some 🦀" at 360px. */}
          <div className="min-w-0 flex-1">
            <p data-testid="team-overlay-name" className="font-display text-sm font-bold text-foreground truncate">{team.name}</p>
            <p className="text-[11px] text-muted-foreground truncate">
              {rec}
              {pf !== null && <> · <span className="tabular-nums">{pf.toFixed(1)}</span> PF</>}
              {pa !== null && <> · <span className="tabular-nums">{pa.toFixed(1)}</span> PA</>}
            </p>
          </div>
          {/* Withheld, not disabled, on your own team: a greyed Trade invites a tap and
              explains nothing. The ROW stays clickable either way -- viewing your own
              roster from standings is a legitimate thing to want. */}
          {!isOwnTeam && onTrade && (
            <Button
              size="sm"
              data-testid="team-overlay-trade"
              className="flex-none shrink-0 whitespace-nowrap text-xs"
              onClick={() => { onOpenChange(false); onTrade(team.id); }}
            >
              Trade
            </Button>
          )}
          <button
            type="button"
            aria-label="Close"
            data-testid="team-overlay-close"
            onClick={() => onOpenChange(false)}
            className="h-11 w-11 -mr-2 flex-none flex items-center justify-center rounded-lg text-muted-foreground hover:text-foreground active:bg-muted"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto">
          {loading && rows.length === 0 ? (
            <p className="px-4 py-6 text-xs text-muted-foreground">Loading roster…</p>
          ) : (
            ordered.map(({ slot, player }, i) => {
            // SAB-1068. The handler goes on the EXISTING row div rather than wrapping the
            // contents in a button. The overlay measures clean at 390/360/1440 (SAB-1005)
            // and adding a DOM node to a flex row is exactly how that stops being true --
            // so the row gains behaviour and no new elements.
            //
            // Close-first, matching the Trade button twenty lines up: the overlay is a
            // Sheet and the detail modal is another, and opening the second while the
            // first is still mounted leaves the user tapping an invisible surface. That
            // ordering is the bug SAB-1045 and SAB-1062 each had to fix once already.
            const openPlayer = player && onPlayerClick
              ? () => { onOpenChange(false); onPlayerClick(player.player_id); }
              : undefined;
            return (
              <div
                key={`${slot}-${i}`}
                className={cn(
                  'flex items-center gap-2 border-b border-border/50 px-3 py-2',
                  openPlayer && 'cursor-pointer hover:bg-muted/40 transition-colors',
                )}
                {...(openPlayer ? {
                  role: 'button' as const,
                  tabIndex: 0,
                  onClick: openPlayer,
                  onKeyDown: (e: KeyboardEvent) => {
                    if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); openPlayer(); }
                  },
                } : {})}
              >
                <span className="w-11 flex-none text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
                  {slotLabel(slot)}
                </span>
                {player ? (
                  <>
                    {/* SAB-1046 follow-up: photo outermost, so the eye lands on the face
                        before the text -- same order as Players and My Team. */}
                    <NFLPlayerAvatar
                      fullName={player.full_name}
                      position={player.position}
                      headshotUrl={player.headshot_url}
                      className="h-8 w-8"
                    />
                    <div className="min-w-0 flex-1">
                      <NFLPlayerMeta
                        fullName={player.full_name}
                        playerId={player.player_id}
                        onOpenNews={(onPlayerNews ?? onPlayerClick) ? () => (onPlayerNews ?? onPlayerClick)!(player.player_id) : undefined}
                        position={player.position}
                        teamAlias={player.nfl_team_abbr}
                        byeWeek={player.bye_week}
                        injuryStatus={player.injury_status}
                        status={player.status}
                      />
                    </div>
                    {/* NULL is not zero -- SAB-811. A player with no projection shows a
                        dash, never 0.0, which would sort and read as a real number. */}
                    <span className="w-12 flex-none text-right text-xs tabular-nums text-muted-foreground">
                      {player.weekly_proj === null ? '--' : player.weekly_proj.toFixed(1)}
                    </span>
                  </>
                ) : (
                  <span className="flex-1 text-xs text-muted-foreground/60">Empty</span>
                )}
              </div>
            );
            })
          )}
        </div>
      </SheetContent>
    </Sheet>
    </ActiveNewsProvider>
  );
}
